/**
 * Read-only tools — let the agent inspect every wedding resource. None of
 * these mutate data, so they never need confirmation.
 */

import { Schema } from "firebase/ai";
import {
  guestSeatingForPlan,
  type Guest,
  type Table,
  type Task,
} from "@amazing-wedding/shared-types";
import type { PlannerTool } from "./registry";
import { asEnum, asString, daysUntil, isoOrNull } from "./helpers";
import { buildTableListEntry, guestSeatNumber } from "../seating/seatingToolHelpers";

const RSVP_VALUES = ["pending", "accepted", "declined"] as const;
const TASK_STATUS = ["todo", "inProgress", "done"] as const;
const TASK_PRIORITY = ["low", "medium", "high"] as const;
const TASK_CATEGORY = [
  "venue",
  "catering",
  "photography",
  "music",
  "flowers",
  "attire",
  "transport",
  "beauty",
  "invitations",
  "ceremony",
  "honeymoon",
  "other",
] as const;

function guestSummary(
  g: Guest,
  tables: Table[],
  allGuests: Guest[],
  planId: string | null,
  legacyPlanId?: string | null,
) {
  const seating = guestSeatingForPlan(g, planId, legacyPlanId);
  return {
    id: g.id,
    name: `${g.firstName} ${g.lastName}`.trim(),
    firstName: g.firstName,
    lastName: g.lastName,
    phone: g.phone ?? null,
    rsvpStatus: g.rsvpStatus,
    mealPreference: g.mealPreference ?? null,
    groupId: g.groupId ?? null,
    hasPlusOne: !!g.plusOne,
    childCount: g.children?.length ?? 0,
    tableId: seating.tableId,
    seatNumber: guestSeatNumber(g, tables, allGuests, planId, legacyPlanId),
    accommodationRequired: g.accommodationRequired,
  };
}

function taskSummary(t: Task) {
  return {
    id: t.id,
    title: t.title,
    status: t.status,
    priority: t.priority,
    category: t.category,
    dueDate: isoOrNull(t.dueDate),
    description: t.description ?? null,
  };
}

export const readTools: PlannerTool[] = [
  {
    declaration: {
      name: "get_wedding_overview",
      description:
        "Get a high-level summary of the whole wedding: couple, date, days remaining, venue, guest RSVP counts, task progress and budget totals. Call this first for broad 'how are we doing' questions.",
    },
    destructive: false,
    describe: () => "Przegląd wesela",
    execute: async (_args, ctx) => {
      const w = ctx.wedding();
      const guests = ctx.guests();
      const tasks = ctx.tasks();
      const budget = ctx.budgetItems();
      const estimated = budget.reduce((s, b) => s + b.estimatedCost, 0);
      const actual = budget.reduce((s, b) => s + b.actualCost, 0);
      const deposits = budget.reduce((s, b) => s + (b.depositPaid ?? 0), 0);
      return {
        couple: `${w.coupleName1} & ${w.coupleName2}`,
        weddingDate: isoOrNull(w.weddingDate),
        daysUntil: daysUntil(w.weddingDate),
        venue: w.venue ?? null,
        currency: w.settings.currency,
        guests: {
          total: guests.length,
          accepted: guests.filter((g) => g.rsvpStatus === "accepted").length,
          declined: guests.filter((g) => g.rsvpStatus === "declined").length,
          pending: guests.filter((g) => g.rsvpStatus === "pending").length,
          withPlusOne: guests.filter((g) => !!g.plusOne).length,
          children: guests.reduce((s, g) => s + (g.children?.length ?? 0), 0),
        },
        tasks: {
          total: tasks.length,
          todo: tasks.filter((t) => t.status === "todo").length,
          inProgress: tasks.filter((t) => t.status === "inProgress").length,
          done: tasks.filter((t) => t.status === "done").length,
        },
        budget: {
          plannedTotal: w.settings.budget,
          estimatedSum: estimated,
          actualSum: actual,
          depositSum: deposits,
          unpaidCount: budget.filter((b) => !b.isPaid).length,
        },
        tableCount: ctx.tables().length,
      };
    },
  },
  {
    declaration: {
      name: "list_guests",
      description:
        "List wedding guests with their IDs. Use this to answer guest questions and to resolve a guest's exact ID before calling update_guest or delete_guest.",
      parameters: Schema.object({
        properties: {
          rsvpStatus: Schema.enumString({
            enum: [...RSVP_VALUES],
            description: "Only return guests with this RSVP status.",
          }),
          groupId: Schema.string({
            description: "Only return guests in this group (ID from list_groups).",
          }),
          search: Schema.string({
            description: "Case-insensitive substring match on the guest's name.",
          }),
        },
        optionalProperties: ["rsvpStatus", "groupId", "search"],
      }),
    },
    destructive: false,
    describe: () => "Lista gości",
    execute: async (args, ctx) => {
      const rsvp = asEnum(args.rsvpStatus, RSVP_VALUES);
      const groupId = asString(args.groupId);
      const search = asString(args.search)?.toLowerCase();
      let guests = ctx.guests();
      if (rsvp) guests = guests.filter((g) => g.rsvpStatus === rsvp);
      if (groupId) guests = guests.filter((g) => g.groupId === groupId);
      if (search) {
        guests = guests.filter((g) =>
          `${g.firstName} ${g.lastName}`.toLowerCase().includes(search),
        );
      }
      return {
        count: guests.length,
        guests: guests.map((g) => {
          const w = ctx.wedding();
          return guestSummary(
            g,
            ctx.tables(),
            ctx.guests(),
            ctx.seatingPlanId(),
            w.legacySeatingPlanId,
          );
        }),
      };
    },
  },
  {
    declaration: {
      name: "list_tasks",
      description:
        "List wedding tasks with their IDs. Use this to answer task questions and to resolve a task's exact ID before calling update_task or delete_task.",
      parameters: Schema.object({
        properties: {
          status: Schema.enumString({
            enum: [...TASK_STATUS],
            description: "Only return tasks with this status.",
          }),
          category: Schema.enumString({
            enum: [...TASK_CATEGORY],
            description: "Only return tasks in this category.",
          }),
          priority: Schema.enumString({
            enum: [...TASK_PRIORITY],
            description: "Only return tasks with this priority.",
          }),
        },
        optionalProperties: ["status", "category", "priority"],
      }),
    },
    destructive: false,
    describe: () => "Lista zadań",
    execute: async (args, ctx) => {
      const status = asEnum(args.status, TASK_STATUS);
      const category = asEnum(args.category, TASK_CATEGORY);
      const priority = asEnum(args.priority, TASK_PRIORITY);
      let tasks = ctx.tasks();
      if (status) tasks = tasks.filter((t) => t.status === status);
      if (category) tasks = tasks.filter((t) => t.category === category);
      if (priority) tasks = tasks.filter((t) => t.priority === priority);
      return { count: tasks.length, tasks: tasks.map(taskSummary) };
    },
  },
  {
    declaration: {
      name: "list_budget",
      description:
        "List budget items with their IDs plus spending totals. Use this to answer budget questions and to resolve a budget item's ID before update_budget_item or delete_budget_item.",
    },
    destructive: false,
    describe: () => "Lista budżetu",
    execute: async (_args, ctx) => {
      const items = ctx.budgetItems();
      return {
        count: items.length,
        plannedTotal: ctx.wedding().settings.budget,
        currency: ctx.wedding().settings.currency,
        estimatedSum: items.reduce((s, b) => s + b.estimatedCost, 0),
        actualSum: items.reduce((s, b) => s + b.actualCost, 0),
        items: items.map((b) => ({
          id: b.id,
          category: b.category,
          supplierName: b.supplierName ?? null,
          estimatedCost: b.estimatedCost,
          actualCost: b.actualCost,
          depositPaid: b.depositPaid ?? 0,
          isPaid: b.isPaid,
          dueDate: isoOrNull(b.dueDate),
          notes: b.notes ?? null,
        })),
      };
    },
  },
  {
    declaration: {
      name: "list_tables",
      description:
        "List seating tables with per-seat occupancy, capacity and free seats. Use before assign_guest_to_seat or other seating changes.",
    },
    destructive: false,
    describe: () => "Lista stołów",
    execute: async (_args, ctx) => {
      const tables = ctx.tables();
      const guests = ctx.guests();
      return {
        count: tables.length,
        tables: tables.map((t) => buildTableListEntry(t, guests)),
      };
    },
  },
  {
    declaration: {
      name: "list_groups",
      description:
        "List guest groups (e.g. 'Bride's family', 'Friends') with their IDs. Use this to resolve a groupId before creating or updating a guest.",
    },
    destructive: false,
    describe: () => "Lista grup gości",
    execute: async (_args, ctx) => {
      const groups = ctx.groups();
      return {
        count: groups.length,
        groups: groups.map((g) => ({ id: g.id, name: g.name, color: g.color })),
      };
    },
  },
  {
    declaration: {
      name: "list_accommodation",
      description: "List accommodation venues and their rooms, with bed counts and occupancy.",
    },
    destructive: false,
    describe: () => "Lista noclegów",
    execute: async (_args, ctx) => {
      const venues = ctx.venues();
      const rooms = ctx.rooms();
      return {
        venues: venues.map((v) => ({
          id: v.id,
          name: v.name,
          address: v.address ?? null,
        })),
        rooms: rooms.map((r) => ({
          id: r.id,
          venueId: r.venueId,
          name: r.name,
          type: r.type,
          bedCount: r.bedCount,
          assigned: r.guestIds.length,
        })),
      };
    },
  },
];
